// src/pages/Level-1.jsx
/**
 * Level 1 - Twinkle Twinkle Little Star (Doron Battle)
 *
 * ✅ V5: Google Analytics instrumentation
 * ✅ V6: Timer removed from free tier — Levels 1–3 are free unlimited
 *        Auth gate kept — login still required to save progress
 */

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import LevelTemplate from "./LevelTemplate";
import DoronHero from "../components/DoronHero";
import AuthModal from "../components/AuthModal";
import { STREAM_VIDEOS } from "../videos/streamVideos";
import { TrophyStorage } from "../services/trophyStorage";
import { sendGAEvent, trackLevelComplete } from "../analytics/analytics";

/* ─────────────────────────────────────────────
   CONFIG
   ───────────────────────────────────────────── */

const LEVEL1_FALLBACK_STREAM_ID = "cb1cb4fbf84c912e5f11e5c215b00d5e";
const LEVEL1_INTRO_STREAM_ID =
  STREAM_VIDEOS?.L1_DORON_BATTLE_INTRO?.id || LEVEL1_FALLBACK_STREAM_ID;
const LEVEL1_VIDEO_SRC = LEVEL1_INTRO_STREAM_ID ? `stream:${LEVEL1_INTRO_STREAM_ID}` : null;
const STAR_QUEST_VIDEO_SRC = "/videos/Star Quest Video.mp4";
const SLEEPING_DORON_IMG = "/images/sleeping-doron.png";

const LEVEL1_MELODY_LINES = [
  { id: 0, notes: ["C", "C", "G", "G", "A", "A", "G"], lyric: "Twinkle Twinkle Little Star" },
  { id: 1, notes: ["F", "F", "E", "E", "D", "D", "C"], lyric: "How I wonder What you are" },
  { id: 2, notes: ["G", "G", "F", "F", "E", "E", "D"], lyric: "Up a- bove the world so high" },
  { id: 3, notes: ["G", "G", "F", "F", "E", "E", "D"], lyric: "Like a Dragon in the sky" },
  { id: 4, notes: ["C", "C", "G", "G", "A", "A", "G"], lyric: "Twinkle Twinkle Little Star" },
  { id: 5, notes: ["F", "F", "E", "E", "D", "D", "C"], lyric: "Doron Sleep, now get the stars!" },
];

const LEVEL1_LYRIC_GROUPS = [
  ["Twinkle", "Twinkle", "Little", "Star"],
  ["How I", "wonder", "What you", "are"],
  ["Up a-", "bove the", "world so", "high"],
  ["Like a", "Dragon", "in the", "sky"],
  ["Twinkle", "Twinkle", "Little", "Star"],
  ["Doron", "Sleep, now", "get the", "stars!"],
];

const LEVEL1_NOTE_GROUP_INDEXES = [
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],
  [{ start: 0, end: 1 }, { start: 2, end: 3 }, { start: 4, end: 5 }, { start: 6, end: 6 }],
];

/* ─────────────────────────────────────────────
   COMPONENT
   ───────────────────────────────────────────── */

export default function Level1() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showStarQuest, setShowStarQuest]   = useState(false);

  // GA refs
  const levelStartTimeRef  = useRef(null);
  const viewFiredRef       = useRef(false);
  const completeFiredRef   = useRef(false);

  // ✅ GA: level_view once on mount
  useEffect(() => {
    if (viewFiredRef.current) return;
    viewFiredRef.current = true;
    sendGAEvent("level_view", {
      level_number: 1,
      level_name: "Level 1",
      logged_in: Boolean(user),
    });
  }, [user]);

  // ────────────────────────────────────────────
  // Handlers
  // ────────────────────────────────────────────

  const handleStartGame = () => {
    sendGAEvent("level_start_clicked", {
      level_number: 1,
      logged_in: Boolean(user),
    });

    if (!user) {
      sendGAEvent("level_start_blocked_login", { level_number: 1 });
      setShowAuthModal(true);
      return;
    }

    // ✅ V6: No timer to start — just record GA timestamp
    levelStartTimeRef.current = Date.now();
    sendGAEvent("level_started", {
      level_number: 1,
      level_name: "Level 1",
      trigger: "logged_in_start",
    });
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    levelStartTimeRef.current = Date.now();
    sendGAEvent("level_started", {
      level_number: 1,
      level_name: "Level 1",
      trigger: "post_login_start",
    });
  };

  const handleLevelComplete = () => {
    TrophyStorage.markLevelComplete(1);

    if (!completeFiredRef.current) {
      completeFiredRef.current = true;
      const timeSpent = levelStartTimeRef.current
        ? Math.round((Date.now() - levelStartTimeRef.current) / 1000)
        : null;
      trackLevelComplete(1, timeSpent);
    }

    setShowStarQuest(true);
  };

  // ────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <LevelTemplate
        levelId={1}
        videoSrc={LEVEL1_VIDEO_SRC}
        melodyLines={LEVEL1_MELODY_LINES}
        lyricGroupsPerLine={LEVEL1_LYRIC_GROUPS}
        noteGroupIndexesPerLine={LEVEL1_NOTE_GROUP_INDEXES}
        previewAudioSrc="/audio/previews/level-1-preview.mp3"
        backgroundImage="/images/Doron_Battle_Background.png"
        renderHero={(heroProps) => <DoronHero {...heroProps} />}
        currentLevelForSidebar={1}
        completionTitle="Doron stirs... the quest begins! 🐉"
        completionBody={
          <>
            The spell worked — but Doron is only sleeping for now.{" "}
            <span className="font-semibold text-[#7C3AED]">Zeb the Zebra</span> needs your help next.
            Levels 2 and 3 are also free — keep going!
          </>
        }
        onComplete={handleLevelComplete}
        startLabel="Start Game"
        onStartGame={handleStartGame}
        mobileMeterBelowPiano
        // ✅ V6: enableTimer and timerAutoStart removed — no time limit on free levels
      />

      {/* Auth Modal — force login, no close button */}
      {showAuthModal && (
        <AuthModal
          onSuccess={handleAuthSuccess}
          onClose={() => setShowAuthModal(false)}
          message="Sign in to save your progress"
          showBenefits={true}
          hideCloseButton={true}
          gaSource="level_1_start_gate"
        />
      )}

      {/* Level 1 Complete — Star Quest Modal */}
      {showStarQuest && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-[28px] shadow-2xl max-w-3xl w-[92%] sm:w-[80%] p-4 sm:p-6 flex flex-col gap-3 sm:gap-4">

            <div className="relative flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg sm:text-xl font-black text-slate-900">
                  Amazing — Doron is asleep! ⭐
                </h2>
                <p className="text-xs sm:text-sm text-slate-600">
                  Levels 2 and 3 are free too — keep playing to earn all 3 free stars!
                </p>
              </div>

              <button
                type="button"
                className="text-xs sm:text-sm text-slate-400 hover:text-slate-700"
                onClick={() => {
                  sendGAEvent("level_complete_modal_dismissed", { level_number: 1 });
                  setShowStarQuest(false);
                }}
              >
                ✕
              </button>

              <img
                src={SLEEPING_DORON_IMG}
                alt="Sleeping Doron"
                className="absolute right-4 top-0 h-10 w-10 sm:h-20 sm:w-20 object-contain drop-shadow-sm"
                draggable={false}
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </div>

            <div className="w-full">
              <video
                className="w-full rounded-2xl shadow-md border border-slate-200"
                src={STAR_QUEST_VIDEO_SRC}
                autoPlay controls controlsList="nodownload" playsInline
                onPlay={() => sendGAEvent("level_complete_video_play", { level_number: 1 })}
                onEnded={() => sendGAEvent("level_complete_video_ended", { level_number: 1 })}
              />
            </div>

            <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
              <span className="text-[10px] sm:text-xs text-slate-500">
                Up next: Zeb the Zebra — play Happy Birthday! 🦓
              </span>
              <button
                type="button"
                onClick={() => {
                  sendGAEvent("level_continue_clicked", { from_level: 1, to_level: 2 });
                  setShowStarQuest(false);
                  navigate("/level-2");
                }}
                className="px-4 sm:px-6 py-1.5 sm:py-2 rounded-full bg-gradient-to-b from-[#8B5CF6] to-[#7C3AED] text-white text-xs sm:text-sm font-extrabold shadow-[0_6px_0_rgba(88,28,135,0.4),0_8px_20px_rgba(124,58,237,0.35)] active:translate-y-[1px]"
              >
                🦓 Level 2 →
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}